import { getPlatformName, PlatformIconComponent } from "@/biz/platform";
import { message, Tooltip } from "antd";
import { CopyIcon, SameInfluencerIcon, StarBgIcon } from "../icons";
import copyToClipboard from "@/utils/copyToClipboard";
import { formatNumber } from "@/biz/tool";
import { useTranslation } from "react-i18next";
import { Tag } from "aha-components";
import { AdsCampaignMatchingAccuracy } from "@/pages/CreateCampaign/shared/config";
import { isFeishuEnv } from "@/utils/feishu";
import BenchmarkTags from "@/pages/CreateCampaign/components/SettingsProfiles/BenchmarkTags";
import CreatorAvatarFrame, {
  resolveCreatorAvatarFrameType,
} from "@/components/CreatorAvatarFrame";

export default function TableCreatorName({
  handleClick,
  item,
  showFollowers = false,
  listShowFollowers = false,
  showCreatorPersonaType = true,
  showUserName = true,
}: {
  handleClick?: Function;
  item: any;
  showFollowers?: boolean;
  listShowFollowers?: boolean;
  showCreatorPersonaType?: boolean;
  showUserName?: boolean;
}) {
  const { t } = useTranslation();
  const userName = item?.userName?.trim().replace(/^@/, "").trim();
  const creatorNameMaxWidthClass = isFeishuEnv()
    ? "max-w-[120px]"
    : "max-w-[140px] md:max-w-[160px]";
  const creatorAvatarFrameType = resolveCreatorAvatarFrameType(item);
  const isMultiChannelOrder = Array.isArray(item?.channels) && item.channels.length > 1;
  const isMultiChannelParent = listShowFollowers && isMultiChannelOrder;
  const isChannelRow = listShowFollowers && item?.__channelRowType === "channel";
  const showAccountLink = showUserName && !!userName && !isChannelRow && !isMultiChannelParent;
  const displayName = isChannelRow
    ? (userName ? `@${userName}` : undefined)
    : item?.creatorName;

  // 将 matchingAccuracy 枚举值转换为本地化文本
  const getMatchingAccuracyLabel = (accuracy: string) => {
    const matchText = t("components.tableCreatorName.match", {
      defaultValue: "match",
    });
    if (accuracy === AdsCampaignMatchingAccuracy.FOCUS_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.focusMode.label", {
          defaultValue: "Focus mode",
        }) +
        " " +
        matchText
      );
    }
    if (accuracy === AdsCampaignMatchingAccuracy.SCALE_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.scaleMode.label", {
          defaultValue: "Scale mode",
        }) +
        " " +
        matchText
      );
    }
    if (accuracy === AdsCampaignMatchingAccuracy.MAX_SCALE_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.maxScaleMode.label", {
          defaultValue: "Max scale mode",
        }) +
        " " +
        matchText
      );
    }
    return accuracy;
  };

  return (
    <div
      className="flex items-center"
      onClick={() => handleClick && handleClick(item)}
    >
      <div className="cursor-pointer mr-3">
        <div
          className={`${listShowFollowers ? "w-14 h-14" : "w-12 h-12"} justify-start items-center gap-3 flex relative flex-shrink-0`}
        >
          <img
            alt=""
            className={`${listShowFollowers ? "w-14 h-14" : "w-12 h-12"} rounded-full flex-shrink-0`}
            src={item?.creatorProfilePictureLink || item?.avatarUrl}
          />
          {!isMultiChannelOrder && !isChannelRow && (
            <div className="absolute right-0 bottom-0">
              <PlatformIconComponent
                platform={item?.platform}
                className="w-[20px] h-[20px]"
              />
            </div>
          )}
          {creatorAvatarFrameType && (
            <div
              className={`${listShowFollowers ? "w-20 h-20 top-[-12px] left-[-12px]" : "w-[72px] h-[72px] top-[-12px] left-[-12px]"} absolute z-[-1] rounded-full`}
            >
              <CreatorAvatarFrame type={creatorAvatarFrameType} size={56} />
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <div className="group/creator-name text-zinc-950 font-semibold cursor-pointer line-clamp-1 flex items-center gap-1">
            {displayName && <Tooltip title={displayName}>
              {isChannelRow && item?.creatorProfileLink ? (
                <a
                  className={`${creatorNameMaxWidthClass} overflow-hidden text-ellipsis whitespace-nowrap !text-[#2B7FFF]`}
                  href={item?.creatorProfileLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {displayName}
                </a>
              ) : (
                <div
                  className={`${creatorNameMaxWidthClass} overflow-hidden text-ellipsis whitespace-nowrap ${isChannelRow ? "text-[#2B7FFF]" : ""}`}
                >
                  {displayName}
                </div>
              )}
            </Tooltip>}
            {listShowFollowers && !isChannelRow && item?.creatorName && (
              <Tooltip title={t("components.tableCreatorName.copyUserName")}>
                <button
                  type="button"
                  aria-label={t("components.tableCreatorName.copyUserName")}
                  className="flex h-5 w-5 shrink-0 items-center justify-center text-[#79716B] opacity-0 pointer-events-none group-hover/creator-name:opacity-100 group-hover/creator-name:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto hover:text-[#292524]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (copyToClipboard(item.creatorName)) {
                      message.success(t("common.messages.copySuccess"));
                    } else {
                      message.error(t("common.messages.copyFail"));
                    }
                  }}
                >
                  <CopyIcon className="h-4 w-4" />
                </button>
              </Tooltip>
            )}
            {item?.creatorMixedScore && item?.creatorDeliveryCount >= 4 && (
              <div className="border border-[#FFD1A9] bg-[#FFF6ED] w-[47px] h-[20px] flex items-center justify-center rounded-md gap-1">
                <StarBgIcon className="text-primary w-[14px] h-[14px]" />
                <div className="justify-center text-primary text-[12px] font-semibold font-['DM_Sans']">
                  {item?.creatorMixedScore}
                </div>
              </div>
            )}
          </div>
          {item?.creatorReferenceFrom && (
            <Tooltip
              color="#000"
              title={
                <div>
                  {t(
                    "components.tableCreatorName.influencerMatchedFromReference",
                  )}{" "}
                  <a
                    className="text-link"
                    href={item?.creatorReferenceFrom}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item?.creatorReferenceFrom}
                  </a>
                </div>
              }
            >
              <span>
                <SameInfluencerIcon className="text-[#009966] flex-shrink-0" />
              </span>
            </Tooltip>
          )}
        </div>
        <div
          className={`flex items-center ${listShowFollowers ? "gap-2" : "gap-6"}`}
        >
          {isChannelRow && (
            <div className="flex items-center gap-1 whitespace-nowrap text-xs">
              <PlatformIconComponent
                platform={item?.platform}
                className="w-4 h-4 flex-shrink-0"
              />
              <span>{getPlatformName(item?.platform)}</span>
            </div>
          )}
          {showAccountLink && (
            <Tooltip title={`@${userName}`}>
              <a
                className={`${listShowFollowers ? "!max-w-[80px] min-w-0 truncate" : "max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap flex-shrink-0"} creator-list-table-content-link inline-block text-xs font-medium !text-[#2B7FFF]`}
                href={item?.creatorProfileLink}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                @{userName}
              </a>
            </Tooltip>
          )}
          {showFollowers && (
            <>
              {item?.followers && (
                <div className="inline-flex justify-start items-center gap-1">
                  <div className="justify-start text-Zinc-950 text-xs font-medium font-['DM_Sans'] leading-none">
                    {formatNumber(item?.followers)}
                  </div>
                  <div className="justify-start text-font&icon-secondary text-xs font-normal font-['DM_Sans'] leading-none">
                    {t("components.tableCreatorName.following")}
                  </div>
                </div>
              )}
              {item?.estimateView && (
                <div className="inline-flex justify-start items-center gap-1">
                  <div className="justify-start text-Zinc-950 text-xs font-medium font-['DM_Sans'] leading-none">
                    {formatNumber(item?.estimateView)}
                  </div>
                  <div className="justify-start text-font&icon-secondary text-xs font-normal font-['DM_Sans'] leading-none">
                    {t("components.tableCreatorName.estimatedViewsPost")}
                  </div>
                </div>
              )}
              {item?.matchingAccuracy && (
                <Tag
                  color={
                    item?.matchingAccuracy ===
                    AdsCampaignMatchingAccuracy.FOCUS_MODE
                      ? "#8E51FF"
                      : item?.matchingAccuracy ===
                          AdsCampaignMatchingAccuracy.SCALE_MODE
                        ? "#FE9A00"
                        : "#00B8DB"
                  }
                  variant="fill"
                  className="whitespace-nowrap"
                >
                  {getMatchingAccuracyLabel(item?.matchingAccuracy)}
                </Tag>
              )}
            </>
          )}
          {listShowFollowers && item?.followers ? (
            <>
              {!isMultiChannelParent && (
                <div className="w-[1px] h-[16px] bg-borderColor flex-shrink-0"></div>
              )}
              <div className="whitespace-nowrap flex-shrink-0">
                <span className="text-[#79716B]">
                  {t("components.tableCreatorName.followers")}
                </span>
                <span className="text-[#1C1917]">
                  {formatNumber(item?.followers)}
                </span>
              </div>
            </>
          ) : null}
        </div>
        {item?.creatorPersonaType && showCreatorPersonaType && (
          <Tooltip title={item.creatorPersonaType}>
            <div className="line-clamp-3">
              <span className="text-[#79716B]">
                {t("components.tableCreatorName.influencerProfile", {
                  defaultValue: "Influencer profile: ",
                })}
              </span>
              <span className="text-[#1C1917]">{item.creatorPersonaType}</span>
            </div>
          </Tooltip>
        )}
        <BenchmarkTags
          type="multiple"
          list={item?.competitorProducts || []}
        />
      </div>
    </div>
  );
}
